import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type BillingInterval, type BillingOverview, type Me, type PlanPrice } from "../lib/api";
import { useSession } from "../lib/session";

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });
const pounds = (pence: number) => `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: pence % 100 ? 2 : 0 })}`;
const per = (i: BillingInterval) => (i === "month" ? "a month" : "a year");

/** Owners choose a plan, see what they pay, and go to Stripe to change payment details or cancel. */
export function Billing({ me }: { me: Me }) {
  const { refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingOverview | null>(null);
  const load = useAction();
  const act = useAction();
  const justPaid = params.get("checkout") === "done";

  const reload = useCallback(() => load.run(async () => setBilling(await api.billing())), [load.run]);
  useEffect(() => void reload(), [reload]);

  // Back from Stripe: its confirmation can take a few seconds to reach us.
  useEffect(() => {
    if (!justPaid || !billing?.enabled || billing.status !== "none") {
      if (justPaid && billing?.enabled && billing.status !== "none") void refresh();
      return;
    }
    const timer = setTimeout(() => void reload(), 2000);
    return () => clearTimeout(timer);
  }, [justPaid, billing, reload, refresh]);

  if (me.user.role !== "owner") return <Navigate to="/today" replace />;

  const go = (get: () => Promise<{ url: string }>) =>
    void act.run(async () => {
      window.location.assign((await get()).url);
    });

  return (
    <>
      <div className="page-head">
        <p className="label">{me.tenant.name}</p>
        <h1 className="display">
          Your plan, <em>plainly</em>.
        </h1>
        <p className="lede">One flat fee for your agency, which covers you, plus a fee for each colleague you add. No contract: cancel whenever you like.</p>
      </div>

      <ErrorNotice error={load.error ?? act.error} />
      {!billing && !load.error && <p className="label">Loading…</p>}
      {billing && !billing.enabled && <p className="notice">Billing isn't switched on yet, so there's nothing to pay.</p>}

      {billing?.enabled && billing.plan === "free" && (
        <section className="section">
          <p className="notice ok">
            Your agency is on a <strong>free plan</strong>
            {billing.planNote ? ` (${billing.planNote})` : ""}. There's nothing to pay, however many colleagues you add. You
            have <span className="figures">{billing.seats}</span> active {billing.seats === 1 ? "person" : "people"}.
          </p>
        </section>
      )}

      {billing?.enabled && billing.plan === "paid" && (
        <>
          {justPaid && billing.status === "none" && <p className="notice">Payment received by Stripe; confirming… this takes a few seconds.</p>}
          {billing.status === "past_due" && (
            <p className="notice" role="alert">
              Your last payment didn't go through. Stripe will try again; update your payment details to sort it out.
              {billing.access.state === "full" && billing.graceEndsAt
                ? ` Everything keeps working until ${dateFmt.format(new Date(billing.graceEndsAt))}.`
                : " Until then your agency is read-only."}
            </p>
          )}

          {["none", "canceled", "unpaid"].includes(billing.status) ? (
            <section className="section" aria-labelledby="choose-h">
              <div className="section-head">
                <h2 id="choose-h" className="label">
                  {billing.status === "none" ? "Choose a plan" : "Restart your subscription"}
                </h2>
                <span className="label figures">
                  {billing.seats} active {billing.seats === 1 ? "person" : "people"}
                </span>
              </div>
              {billing.status !== "none" && (
                <p className="notice">Your subscription has ended, so your agency is read-only. Your data is all still here.</p>
              )}
              <div className="plans">
                {(["month", "year"] as const).map((i) => (
                  <PlanCard key={i} price={billing.prices[i]} seats={billing.seats} monthly={billing.prices.month} pending={act.pending} onChoose={() => go(() => api.checkout(i))} />
                ))}
              </div>
              <p className="fine-print">
                You'll pay securely on Stripe by card or Direct Debit. Adding a colleague later adds {pounds(billing.prices.month.additional)} {per("month")} (or{" "}
                {pounds(billing.prices.year.additional)} {per("year")}), charged for the rest of the period; removing someone credits it back. Prices don't include
                VAT because we aren't VAT-registered.
              </p>
            </section>
          ) : (
            <section className="section" aria-labelledby="current-h">
              <div className="section-head">
                <h2 id="current-h" className="label">
                  Current plan
                </h2>
                <button type="button" className="link-btn" disabled={act.pending} onClick={() => go(api.billingPortal)}>
                  Manage billing →
                </button>
              </div>
              {billing.interval && (
                <dl className="ledger">
                  <div>
                    <dt className="label">You pay</dt>
                    <dd className="figure-xl">{pounds(billing.prices[billing.interval].total)}</dd>
                    <dd className="secondary">
                      {per(billing.interval)} for {billing.seats} {billing.seats === 1 ? "person" : "people"}
                    </dd>
                  </div>
                  <div>
                    <dt className="label">Plan</dt>
                    <dd>{billing.interval === "month" ? "Monthly" : "Annual"}</dd>
                    {billing.periodEnd && (
                      <>
                        <dt className="label spaced-top">{billing.cancelAtPeriodEnd ? "Ends on" : "Renews on"}</dt>
                        <dd>{dateFmt.format(new Date(billing.periodEnd))}</dd>
                      </>
                    )}
                  </div>
                </dl>
              )}
              <p className="fine-print">
                {pounds(billing.prices[billing.interval ?? "month"].first)} for your agency, including you, and{" "}
                {pounds(billing.prices[billing.interval ?? "month"].additional)} for each other active person. Deactivating someone on the Team page stops
                their charge. <strong>Manage billing</strong> opens Stripe, where you can change your card or Direct Debit, download invoices, switch between
                monthly and annual, or cancel.
              </p>
            </section>
          )}
        </>
      )}
      {justPaid && (
        <p className="spaced-top">
          <button type="button" className="link-btn" onClick={() => setParams({})}>
            Dismiss
          </button>
        </p>
      )}
    </>
  );
}

function PlanCard({ price, seats, monthly, pending, onChoose }: { price: PlanPrice; seats: number; monthly: PlanPrice; pending: boolean; onChoose: () => void }) {
  const saving = price.interval === "year" ? monthly.total * 12 - price.total : 0;
  return (
    <div className="plan">
      <p className="label">{price.interval === "month" ? "Monthly" : "Annual"}</p>
      <p className="plan-price">
        <span className="figures">{pounds(price.total)}</span> <span className="per">{per(price.interval)}</span>
      </p>
      <p className="secondary">
        {pounds(price.first)} for your agency
        {seats > 1 && ` + ${seats - 1} × ${pounds(price.additional)}`}
        {saving > 0 && <> · saves {pounds(saving)}</>}
      </p>
      <button type="button" className="btn" disabled={pending} onClick={onChoose}>
        Pay {price.interval === "month" ? "monthly" : "yearly"}
      </button>
    </div>
  );
}
